// Product fact-sheet — assembled from the CMS-friendly product schema.
//
// Strategy:
//   1. Mandatory rows (Gamme, Marque, Type, Public cible, Usage, Préparation,
//      Conservation) always come back so the existing prudent fiche
//      technique keeps working even when the product has no enriched data.
//   2. Optional rows (Référence, Format, Conditionnement, Unités/colis,
//      Colis/palette, Poids, Température, Origine, Certifications,
//      Allergènes, Disponibilité) come back ONLY when the product has a
//      real value. Empty strings and empty arrays return null so the
//      renderer skips the row — no fabricated lines, no blank lines.
//
// Editing a product through the future CMS adds rows automatically; the 255
// other products keep the same 7-line fiche as today.

const CATEGORY_PROFILES = {
  frites: {
    typeProduit: "Surgelé · Friteuse pro",
    usage: "Friteuse · Service rapide · Rushes intenses",
    preparation: "Bain de friture, cuisson rapide. Calibrage régulier en service.",
  },
  viandes: {
    typeProduit: "Viande pro · Boucherie",
    usage: "Grill · Plancha · Smash · Restauration rapide",
    preparation: "Service chaud. Adapté aux volumes de coup de feu.",
  },
  volailles: {
    typeProduit: "Pané pro · IQF ou frais",
    usage: "Friteuse · Four · Service à la commande",
    preparation: "Cuisson rapide, croustillance stable.",
  },
  "tortillas-et-pains": {
    typeProduit: "Base sandwich pro",
    usage: "Burger · Kebab · Tacos · Wrap",
    preparation: "Texture stable, tenue garnissage.",
  },
  sauce: {
    typeProduit: "Sauce condiment",
    usage: "Accompagnement · Garnissage · Personnalisation carte",
    preparation: "Service direct ou dosage portion.",
  },
  conserve: {
    typeProduit: "Conserve pro grand volume",
    usage: "Stock long · Préparations froides ou chaudes",
    preparation: "Prêt à l'emploi, conservation longue.",
  },
  huile: {
    typeProduit: "Huile pro friture",
    usage: "Bain de friture · Cuisson grand volume",
    preparation: "Tenue thermique au rythme service.",
  },
  "produits-laitiers": {
    typeProduit: "Frais pro · Tranches ou IQF",
    usage: "Burger · Wrap · Plats chauds",
    preparation: "Prêt à servir, format pro.",
  },
  dessert: {
    typeProduit: "Dessert portion individuelle",
    usage: "Carte dessert · Vente additionnelle",
    preparation: "Sans préparation cuisine.",
  },
  specialites: {
    typeProduit: "Spécialité signature",
    usage: "Friteuse · Snacking signature",
    preparation: "Prêt à frire ou réchauffer.",
  },
  appetizers: {
    typeProduit: "Snack avant-plat",
    usage: "Friteuse · Entrée snacking",
    preparation: "Prêt à frire, service rapide.",
  },
  emballages: {
    typeProduit: "Emballage take-away",
    usage: "Take-away · Drive · Livraison",
    preparation: "Solide en sortie, propre à l'arrivée.",
  },
};

const DEFAULT_PROFILE = {
  typeProduit: "Produit pro restauration rapide",
  usage: "Restauration rapide · Snacking",
  preparation: "Format pro pour service rapide.",
};

// Coerce any field to a row value, or null if the row should be skipped.
const toRow = (v) => {
  if (v == null) return null;
  if (Array.isArray(v)) return v.length ? v.join(" · ") : null;
  if (typeof v === "string") {
    const trimmed = v.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof v === "number") return Number.isFinite(v) && v !== 0 ? String(v) : null;
  return null;
};

export const generateProductSpecs = (product) => {
  if (!product) return null;
  const profile = CATEGORY_PROFILES[product.category] || DEFAULT_PROFILE;

  // Mandatory rows — prefer real product field, fallback to category profile.
  const conservation = toRow(product.conservation) || "À confirmer selon référence et conditionnement";
  const preparation = toRow(product.mode_preparation) || profile.preparation;

  return {
    // Always present (mandatory rows)
    gamme: product.category_label || "—",
    marque: product.brand || "—",
    typeProduit: profile.typeProduit,
    publicCible: "Restauration rapide · Snacking · Pros B2B",
    usage: profile.usage,
    preparation,
    conservation,
    // Optional rows — null when the product has no real value
    reference: toRow(product.reference_interne),
    format: toRow(product.format),
    conditionnement: toRow(product.conditionnement),
    unitesParColis: toRow(product.unites_par_colis),
    colisParPalette: toRow(product.colis_par_palette),
    poids: toRow(product.poids_unite),
    temperature: toRow(product.temperature_conservation),
    origine: toRow(product.origine),
    certifications: toRow(product.certifications),
    allergenes: toRow(product.allergenes),
    disponibilite: toRow(product.disponibilite),
  };
};
